/* eslint-disable no-prototype-builtins */
/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import {
    getCurrentCartState,
    hasQuoteRequiredProducts,
    isCartEmpty,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductAvailability from "@insite/content-library/Components/ProductAvailability";
import ProductDescription from "@insite/content-library/Components/ProductDescription";
import ProductImage from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import CartLineQuantity from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import Link from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography from "@insite/mobius/Typography";
import MiniCartActionButtons from "@monsma/App/Components/XngMiniCart/XngMiniCartActions";
import { minicartStyles } from "@monsma/App/Components/XngMiniCart/XngMiniCartStyles";
import { xAppState } from "@monsma/App/Store/Reducers";
import FancyScroll from "@monsma/XDL/Components/FancyScroll";
import Notify from "@monsma/XDL/Components/Notify";
import React from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface MiniCartHeadingProps {
    count: number;
    subTotal?: string;
    hasQuoteRequiredProducts?: boolean;
    lessThanOrder: boolean;
    cartNotPriced: boolean;
}

interface MiniCartLinesProps {
    setShowSpinner: any;
    cartLine: CartLineModel;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine: (id: string) => void;
    cartType?: string;
    cart?: any;
}

const MiniCartHeading = ({ count, hasQuoteRequiredProducts, lessThanOrder, cartNotPriced }: MiniCartHeadingProps) => {
    return (
        <StyledWrapper {...minicartStyles.miniCartHeadingStyles}>
            <StyledWrapper {...minicartStyles.miniCartHeadingWrapperStyles}>
                <Typography as="strong">{count || 0}</Typography>
                <Typography as="strong">{translate(` ${count > 1 ? "Items" : "Item"} Total`)} </Typography>
            </StyledWrapper>
            {(hasQuoteRequiredProducts || lessThanOrder || cartNotPriced) && (
                <StyledWrapper>
                    {hasQuoteRequiredProducts && (
                        <Notify
                            {...minicartStyles.notificationContainers}
                            messageType="warning"
                            hideCloseButton={false}
                            body={siteMessage("Custom_Cart_has_quote_required_products")}
                        />
                    )}
                    {lessThanOrder && count > 0 && (
                        <Notify
                            {...minicartStyles.notificationContainers}
                            messageType="warning"
                            hideCloseButton={false}
                            body={siteMessage("Cart_LessThanOrderThreshold")}
                        />
                    )}
                    {cartNotPriced && count > 0 && (
                        <Notify
                            {...minicartStyles.notificationContainers}
                            messageType="warning"
                            hideCloseButton={false}
                            body={siteMessage("Cart_PriceNotAvailable")}
                        />
                    )}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const MiniCartLine: React.FC<MiniCartLinesProps> = ({ cartLine, removeCartLine, cart, updateCartLine }) => {
    const isInactive = !cartLine.isActive;
    const isRestricted = cartLine.isRestricted;
    let isEditable = true;

    if (isInactive || isRestricted) {
        isEditable = false;
    }

    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            if (qtyOrdered <= 0) {
                removeCartLine(cartLine.id);
            } else {
                updateCartLine({
                    cartLine: {
                        ...cartLine,
                        qtyOrdered,
                    },
                });
            }
        }
    };

    return (
        <StyledWrapper {...minicartStyles.cartLineStyles}>
            <StyledWrapper>
                <StyledWrapper {...minicartStyles.cartLineImageStyles}>
                    <ProductImage product={cartLine} extendedStyles={minicartStyles.productImageStyles} />
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...minicartStyles.cartLineMainSectionStyles}>
                <StyledWrapper {...minicartStyles.cartLineDescriptionNumbersWrapper}>
                    <ProductPartNumbers
                        productNumber={cartLine.erpNumber}
                        customerProductNumber={cartLine.customerName}
                        manufacturerItem={cartLine.manufacturerItem}
                        showCustomerName={false}
                        showManufacturerItem={false}
                    />
                    <StyledWrapper {...minicartStyles.cartLineRemoveSectionWrapper}>
                        <Link
                            className="remove"
                            onClick={() => removeCartLine(cartLine.id)}
                            {...minicartStyles.cartLineRemoveLinkStyles}
                        >
                            {translate("Remove")}
                        </Link>
                    </StyledWrapper>
                    <ProductDescription product={cartLine} extendedStyles={minicartStyles.productDescriptionStyles} />
                </StyledWrapper>
                {!cartLine.quoteRequired && (
                    <ProductAvailability
                        productId={cartLine.productId!}
                        availability={cartLine.availability!}
                        unitOfMeasure={cartLine.unitOfMeasure}
                        trackInventory={cartLine.trackInventory}
                        failedToLoadInventory={cart.failedToGetRealTimeInventory}
                        extendedStyles={{
                            container: {
                                gap: 0,
                                css: css`
                                    flex-grow: 0;
                                    margin-top: 30px;
                                `,
                            },
                        }}
                    />
                )}
                <StyledWrapper {...minicartStyles.cartLineQtyPriceSectionStylesWrapper}>
                    <StyledWrapper {...minicartStyles.cartLineQtyWrapper}>
                        <CartLineQuantity
                            cart={cart}
                            extendedStyles={minicartStyles.cartLineQtyStyle}
                            editable={isEditable}
                            onQtyOrderedChange={qtyOrderedChangeHandler}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...minicartStyles.cartLinePriceWrapper}>
                        <ProductPrice
                            showLabel={false}
                            product={cartLine}
                            extendedStyles={minicartStyles.productPrice}
                        />
                    </StyledWrapper>
                </StyledWrapper>
                {(isInactive || isRestricted) && (
                    <StyledWrapper {...minicartStyles.cartLineErrorMsgsStyles}>
                        {isInactive && (
                            <StyledWrapper className="error-message">
                                <Typography as="span" color="danger.main">
                                    {siteMessage("Custom_Mini_Cart_InactiveProduct_Msg")}
                                </Typography>
                            </StyledWrapper>
                        )}
                        {isRestricted && (
                            <StyledWrapper className="error-message">
                                <Typography as="span" color="danger.main">
                                    {siteMessage("Custom_Mini_Cart_RestrictedProduct_Msg")}
                                </Typography>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

/**
 *  Mini-cart component Main entrance
 */
const mapStateToProps = (state: xAppState) => {
    const cartState = getCurrentCartState(state);
    const cart = cartState.value;
    return {
        cart,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        isLoading: getCurrentCartState(state).isLoading,
        isEmpty: isCartEmpty(cart),
        hasQuoteRequiredProducts: hasQuoteRequiredProducts(cart),
        isRemovingCartLine: state.pages.cart?.hasOwnProperty("isRemovingCartLine")
            ? state.pages.cart?.isRemovingCartLine
            : null,
    };
};

const mapDispatchToProps = {
    updateCartLine,
    removeCartLine,
};

type OwnProps = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const XngMiniCart: React.FC<OwnProps> = ({
    cart,
    updateCartLine,
    removeCartLine,
    isRemovingCartLine,
    hasQuoteRequiredProducts,
    isLoading,
    cartPageUrl,
}) => {
    const [showSpinner, setShowSpinner] = React.useState(false);
    const count = cart?.totalCountDisplay;
    const [belowOrderThreshold, setOrderThreshold] = React.useState(false);
    const [removedCartId, setRemovedCartId] = React.useState("");

    const handleRemoveLineWithSpinner = (cartLineId: string) => {
        setRemovedCartId(cartLineId);
        removeCartLine({ cartLineId });
    };

    React.useEffect(() => {
        const orderMin = cart?.properties["orderLessThanMinimumOrderValue"] === "true";

        setOrderThreshold(orderMin);
    }, [cart]);

    React.useEffect(() => {
        if (isRemovingCartLine && isRemovingCartLine?.hasOwnProperty(removedCartId)) {
            setShowSpinner(true);
        } else {
            setShowSpinner(false);
        }
    }, [isRemovingCartLine]);

    const scrollRef = React.useRef<any>(null);

    return (
        <StyledWrapper {...minicartStyles.miniCartContainerStyles}>
            <MiniCartHeading
                count={count || 0}
                lessThanOrder={belowOrderThreshold}
                hasQuoteRequiredProducts={hasQuoteRequiredProducts}
                cartNotPriced={cart?.cartNotPriced || false}
            />
            <StyledWrapper {...minicartStyles.fancyWrapperStyles}>
                <StyledWrapper ref={scrollRef} {...minicartStyles.fancyOverflowStyles}>
                    <FancyScroll parentRef={scrollRef} />

                    {(showSpinner || isLoading) && (
                        <StyledWrapper {...minicartStyles.spinnerWrapper}>
                            <LoadingSpinner color="primary.main" />
                        </StyledWrapper>
                    )}
                    {cart && cart?.cartLines?.length ? (
                        cart.cartLines.map((cartLine: CartLineModel) => {
                            return (
                                <CartLineContext.Provider value={cartLine} key={cartLine.id}>
                                    <MiniCartLine
                                        setShowSpinner={setShowSpinner}
                                        cartType={cart.type}
                                        cartLine={cartLine}
                                        key={cartLine.id}
                                        removeCartLine={handleRemoveLineWithSpinner}
                                        updateCartLine={updateCartLine}
                                        cart={cart}
                                    />
                                </CartLineContext.Provider>
                            );
                        })
                    ) : (
                        <StyledWrapper
                            css={css`
                                padding: 15px 15px 0;
                                margin: 0 10px;
                            `}
                        >
                            <Notify
                                messageType="info"
                                hideCloseButton={true}
                                body={siteMessage("Custom_Empty_Mini_Cart_Msg")}
                            />
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <MiniCartActionButtons cartPageUrl={cartPageUrl} />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(XngMiniCart);
